import type {
  CreateFlowConfigRequest,
  FlowStepRequest,
  LLMFlowStepConfigRequest,
  OCRFlowStepConfigRequest,
  LLMConfigOptions,
  LLMConfigOptionsViewModel,
  AgentExecutionRequest,
} from "@models/doctype";

export interface FlowConfigFormData {
  flowBaseId?: string | null;
  flowType: string;
  flowDescription?: string | null;
  steps: FlowStepFormData[];
}

export interface FlowStepFormData {
  stepOrder: number;
  stepType: string;
  llmStepConfig?: LLMStepConfigFormData;
  ocrStepConfig?: OCRStepConfigFormData;
}

export interface LLMStepConfigFormData {
  provider: string;
  systemPrompt: string;
  userPrompt: string;
  modelName: string;
  configOptions?: LLMConfigOptionsViewModel | null;
  responseSchema?: Record<string, unknown> | null;
  executions?: AgentExecutionFormData[] | null;
}

export interface OCRStepConfigFormData {
  provider: string;
  modelName: string;
  ocrOptions?: Record<string, unknown> | null;
}

export interface AgentExecutionFormData {
  domainKey: string;
  userPrompt: string;
  responseSchema: Record<string, unknown> | null;
  responseSchemaText: string;
  systemPrompt: string;
  modelName: string;
  configOptions?: LLMConfigOptionsViewModel | null;
}

export const buildCreateFlowConfigRequest = (
  formData: FlowConfigFormData,
): CreateFlowConfigRequest => {
  return {
    flow_base_id: formData.flowBaseId || null,
    flow_type: formData.flowType,
    flow_description: formData.flowDescription || null,
    steps: formData.steps.map(buildFlowStepRequest),
  };
};

const buildFlowStepRequest = (stepData: FlowStepFormData): FlowStepRequest => {
  const request: FlowStepRequest = {
    step_order: stepData.stepOrder,
    step_type: stepData.stepType,
  };

  if (stepData.llmStepConfig) {
    request.llm_step_config = buildLLMStepConfigRequest(stepData.llmStepConfig);
  }

  if (stepData.ocrStepConfig) {
    request.ocr_step_config = buildOCRStepConfigRequest(stepData.ocrStepConfig);
  }

  return request;
};

const buildLLMStepConfigRequest = (
  config: LLMStepConfigFormData,
): LLMFlowStepConfigRequest => {
  const request: LLMFlowStepConfigRequest = {
    provider: config.provider,
    system_prompt: config.systemPrompt,
    user_prompt: config.userPrompt,
    model_name: config.modelName,
  };

  if (config.configOptions) {
    request.config_options = buildLLMConfigOptions(config.configOptions);
  }

  if (config.responseSchema) {
    request.response_schema = config.responseSchema;
  }

  if (config.executions?.length) {
    request.executions = config.executions.map(buildAgentExecutionRequest);
  }

  return request;
};

const buildOCRStepConfigRequest = (
  config: OCRStepConfigFormData,
): OCRFlowStepConfigRequest => {
  const request: OCRFlowStepConfigRequest = {
    provider: config.provider,
    model_name: config.modelName,
  };

  if (config.ocrOptions) {
    request.ocr_options = config.ocrOptions;
  }

  return request;
};

const buildAgentExecutionRequest = (
  agent: AgentExecutionFormData,
): AgentExecutionRequest => {
  const request: AgentExecutionRequest = {
    domain_key: agent.domainKey,
    user_prompt: agent.userPrompt,
    response_schema: agent.responseSchema || {},
  };

  if (agent.systemPrompt) {
    request.system_prompt = agent.systemPrompt;
  }

  if (agent.modelName) {
    request.model_name = agent.modelName;
  }

  if (agent.configOptions) {
    request.config_options = buildLLMConfigOptions(agent.configOptions);
  }

  return request;
};

const buildLLMConfigOptions = (
  viewModel: LLMConfigOptionsViewModel,
): LLMConfigOptions => {
  return {
    temperature: viewModel.temperature,
    top_p: viewModel.topP,
    top_k: viewModel.topK,
    max_output_tokens: viewModel.maxOutputTokens,
    thinking_budget: viewModel.thinkingBudget,
    seed: viewModel.seed,
  };
};

export const buildLLMConfigOptionsViewModel = (
  options: LLMConfigOptions | null,
): LLMConfigOptionsViewModel | null => {
  if (!options) return null;

  return {
    temperature: options.temperature,
    topP: options.top_p,
    topK: options.top_k,
    maxOutputTokens: options.max_output_tokens,
    thinkingBudget: options.thinking_budget,
    seed: options.seed,
  };
};
